import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../context/StoreContext';
import { inventoryAPI, productsAPI, extractErrorMessage } from '../api';

const Inventory = () => {
  const { selectedStore } = useStore();
  const [inventory, setInventory] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdjust, setShowAdjust] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [adjustment, setAdjustment] = useState(0);

  // Helper functions
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  const getStockBadge = (available, reorderLevel) => {
    if (available === 0) return 'badge-danger';
    if (available <= reorderLevel) return 'badge-warning';
    return 'badge-success';
  };

  const getStockLabel = (available, reorderLevel) => {
    if (available === 0) return 'Out of Stock';
    if (available <= reorderLevel) return 'Low Stock';
    return 'In Stock';
  };

  const renderIcon = (iconName) => {
    const icons = {
      package: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M16.5 9.4l-9-5.19M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>
      ),
      alertTriangle: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      ),
      xCircle: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      ),
      checkCircle: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      ),
      boxes: (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>
      ),
      sliders: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="4" y1="21" x2="4" y2="14" />
          <line x1="4" y1="10" x2="4" y2="3" />
          <line x1="12" y1="21" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12" y2="3" />
          <line x1="20" y1="21" x2="20" y2="16" />
          <line x1="20" y1="12" x2="20" y2="3" />
          <line x1="1" y1="14" x2="7" y2="14" />
          <line x1="9" y1="8" x2="15" y2="8" />
          <line x1="17" y1="16" x2="23" y2="16" />
        </svg>
      ),
    };
    return icons[iconName] || null;
  };

  // Calculate stats
  const enrichedInventory = inventory.map(item => {
    const product = products.find(p => p.id === item.product_id);
    const available = item.quantity_in_stock - item.quantity_reserved;
    return {
      ...item,
      productName: product?.name || `Product #${item.product_id}`,
      available,
      reorderLevel: product?.reorder_level || 10,
    };
  });

  const stats = {
    totalItems: enrichedInventory.length,
    inStock: enrichedInventory.filter(i => i.available > i.reorderLevel).length,
    lowStock: enrichedInventory.filter(i => i.available > 0 && i.available <= i.reorderLevel).length,
    outOfStock: enrichedInventory.filter(i => i.available === 0).length,
  };

  const loadInventory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await inventoryAPI.list(selectedStore.id);
      setInventory(response.data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load inventory'));
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  const loadProducts = useCallback(async () => {
    try {
      const response = await productsAPI.list(selectedStore.id);
      setProducts(response.data);
    } catch (err) {
      console.error('Failed to load products');
    }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedStore) {
      loadInventory();
      loadProducts();
    }
  }, [selectedStore, loadInventory, loadProducts]);

  const handleAdjust = async () => {
    if (!selectedProduct || adjustment === 0) return;
    try {
      await inventoryAPI.update(selectedStore.id, selectedProduct.id, { adjustment });
      loadInventory();
      setShowAdjust(false);
      setSelectedProduct(null);
      setAdjustment(0);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to adjust inventory'));
    }
  };

  const openAdjustModal = (item) => {
    const product = products.find(p => p.id === item.product_id);
    setSelectedProduct({ ...item, name: product?.name || 'Unknown' });
    setShowAdjust(true);
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div><span>Loading inventory...</span></div>;
  }

  return (
    <div className="page-modern">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Inventory</h1>
          <p className="page-subtitle">Monitor and manage your stock levels across all products</p>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Stats Row */}
      <div className="page-stats-row">
        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--primary)' }}>
            {renderIcon('package')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">Total Items</div>
            <div className="page-stat-value">{formatNumber(stats.totalItems)}</div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--success)' }}>
            {renderIcon('checkCircle')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">In Stock</div>
            <div className="page-stat-value">{formatNumber(stats.inStock)}</div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--warning)' }}>
            {renderIcon('alertTriangle')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">Low Stock</div>
            <div className="page-stat-value">{formatNumber(stats.lowStock)}</div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--danger)' }}>
            {renderIcon('xCircle')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">Out of Stock</div>
            <div className="page-stat-value">{formatNumber(stats.outOfStock)}</div>
          </div>
        </div>
      </div>

      {/* Data Table Card */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Stock Levels ({enrichedInventory.length})</h3>
          <div className="card-header-actions">
            <button className="btn btn-sm btn-secondary">Export</button>
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {enrichedInventory.length === 0 ? (
            <div className="page-empty-state">
              <div className="page-empty-icon">
                {renderIcon('boxes')}
              </div>
              <div className="page-empty-title">No inventory tracked</div>
              <p className="page-empty-text">
                Start tracking stock levels by adding inventory records for your products
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="table-container hidden-mobile">
                <table>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th style={{ textAlign: 'center' }}>In Stock</th>
                      <th style={{ textAlign: 'center' }}>Reserved</th>
                      <th style={{ textAlign: 'center' }}>Available</th>
                      <th>Condition</th>
                      <th>Status</th>
                      <th style={{ width: '80px', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrichedInventory.map(item => (
                      <tr key={item.id}>
                        <td className="font-medium">{item.productName}</td>
                        <td style={{ textAlign: 'center' }}>{item.quantity_in_stock}</td>
                        <td style={{ textAlign: 'center' }}>{item.quantity_reserved}</td>
                        <td style={{ textAlign: 'center', fontWeight: 600 }}>{item.available}</td>
                        <td>{item.condition_status || 'Good'}</td>
                        <td>
                          <span className={`badge ${getStockBadge(item.available, item.reorderLevel)}`}>
                            {getStockLabel(item.available, item.reorderLevel)}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="table-action-btn"
                            onClick={() => openAdjustModal(item)}
                            title="Adjust inventory"
                            style={{ width: 'auto', padding: '0 12px' }}
                          >
                            {renderIcon('sliders')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="mobile-cards hidden-desktop">
                {enrichedInventory.map(item => (
                  <div className="mobile-card" key={item.id}>
                    <div className="mobile-card-header">
                      <div className="mobile-card-title">{item.productName}</div>
                      <span className={`badge ${getStockBadge(item.available, item.reorderLevel)}`}>
                        {getStockLabel(item.available, item.reorderLevel)}
                      </span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">In Stock</span>
                      <span>{item.quantity_in_stock}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Reserved</span>
                      <span>{item.quantity_reserved}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Available</span>
                      <span className="font-bold">{item.available}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Condition</span>
                      <span>{item.condition_status || 'Good'}</span>
                    </div>
                    <div className="mobile-card-actions">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => openAdjustModal(item)}
                      >
                        Adjust Inventory
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Adjust Modal */}
      {showAdjust && (
        <div className="modal-overlay" onClick={() => setShowAdjust(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Adjust Inventory</h3>
              <button className="close-btn" onClick={() => setShowAdjust(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '20px' }}>
                <strong>Product:</strong> {selectedProduct?.name}<br />
                <strong>Current Stock:</strong> {selectedProduct?.quantity_in_stock}
              </p>
              <div className="form-group">
                <label className="form-label">Adjustment (+ to add, - to subtract)</label>
                <input
                  type="number"
                  className="form-input"
                  value={adjustment}
                  onChange={(e) => setAdjustment(parseInt(e.target.value) || 0)}
                  placeholder="Enter adjustment"
                />
              </div>
              {adjustment !== 0 && (
                <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                  New stock level will be: <strong>{selectedProduct?.quantity_in_stock + adjustment}</strong>
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAdjust(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdjust}>Apply Adjustment</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
